import { useState } from 'react'
import type { PromoCode } from '../promo/codes'
import { openPromoModal } from '../promo/modal'
import { usePromo } from '../promo/store'
import { track } from '../runtime/analytics'
import { CloseIcon } from './ui/PromoIcons'

/**
 * The promo that stays on screen, at the edge of the home page: a short
 * call to sign up, in the applied bar's green and blue, where a click opens
 * the same sign-up modal as the bar and the pill. Webflow's fixed element
 * holds it, so it sets no position of its own.
 *
 * It only ever offers: once the promo is active it's gone, since the bar
 * already says it's applied. Its × hides it for the rest of the visit, in
 * sessionStorage, so it's back on the visitor's next one.
 */

export interface StickyPromoProps {
  /**
   * The Webflow modal it opens (`data-modal`), as a CSS selector: the site's
   * `.contact-modal5_component` without it, one for every visitor, or one per
   * code as JSON, as the bar takes it.
   */
  modal?: unknown
}

/** Set once the visitor has closed it, in this tab, until the tab closes. */
export const STICKY_DISMISSED_KEY = 'cv.promo-sticky.v1'

const WORDING: Record<PromoCode, string> = {
  WELCOME40: 'Get 40% OFF',
  FIRSTFREE: 'Get First Kit FREE',
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(STICKY_DISMISSED_KEY) === 'dismissed'
  } catch {
    // Storage throws in private-mode Safari and when it is blocked.
    return false
  }
}

function saveDismissed(): void {
  try {
    sessionStorage.setItem(STICKY_DISMISSED_KEY, 'dismissed')
  } catch {
    // It still goes for this page; it just comes back on the next.
  }
}

export function StickyPromo({ modal }: StickyPromoProps) {
  const code = usePromo((state) => state.code)
  const active = usePromo((state) => state.active)
  const [dismissed, setDismissed] = useState(readDismissed)

  if (active || dismissed) return null

  function open() {
    track({ kind: 'select_promotion', code, component: 'StickyPromo' })
    openPromoModal(modal, code, 'StickyPromo')
  }

  function dismiss() {
    track({ kind: 'promo_dismissed', code })
    saveDismissed()
    setDismissed(true)
  }

  return (
    <div
      className="flex w-max items-center rounded-r-full bg-promo-applied text-promo-ink"
      data-testid="sticky-promo"
    >
      <button
        type="button"
        onClick={open}
        className="rounded-full py-3.5 pr-3 pl-12 text-lg leading-snug font-medium whitespace-nowrap underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-promo-ink focus-visible:ring-inset"
        data-testid="promo-offer"
      >
        {WORDING[code]}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Close the offer"
        className="mr-3 rounded-full p-2.5 outline-none transition hover:bg-white/30 focus-visible:ring-2 focus-visible:ring-promo-ink"
        data-testid="promo-dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  )
}
